/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import { __decorate, __param, __metadata } from 'tslib';
import { Directive, Optional, TemplateRef, Input, Component, ContentChild, ElementRef, NgModule } from '@angular/core';
import { drawDOM, exportPDF } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { validatePackage } from '@progress/kendo-licensing';

/**
 * @hidden
 */
import * as ɵngcc0 from '@angular/core';

const _c0 = ["*"];
const packageMetadata = {
    name: '@progress/kendo-angular-pdf-export',
    productName: 'Kendo UI for Angular',
    productCodes: ['KENDOUIANGULAR', 'KENDOUICOMPLETE'],
    publishDate: 1646219273,
    version: '',
    licensingDocsUrl: 'https://www.telerik.com/kendo-angular-ui/my-license/?utm_medium=product&utm_source=kendoangular&utm_campaign=kendo-ui-angular-purchase-license-keys-warning'
};

let PDFExportTemplateDirective = class PDFExportTemplateDirective {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
};
PDFExportTemplateDirective.ɵfac = function PDFExportTemplateDirective_Factory(t) { return new (t || PDFExportTemplateDirective)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.TemplateRef, 8)); };
PDFExportTemplateDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: PDFExportTemplateDirective, selectors: [["", "kendoPDFTemplate", ""]] });
PDFExportTemplateDirective = __decorate([ __param(0, Optional()),
    __metadata("design:paramtypes", [TemplateRef])
], PDFExportTemplateDirective);

const FIELDS = ['bottom', 'left', 'right', 'top'];
/**
 * Represents the Kendo UI PDFMargin component for Angular.
 */
let PDFExportMarginComponent = class PDFExportMarginComponent {
    /**
     * @hidden
     */
    get options() {
        const options = {};
        for (let idx = 0; idx < FIELDS.length; idx++) {
            const field = FIELDS[idx];
            const value = this[field];
            if (typeof value !== 'undefined') {
                options[field] = value;
            }
        }
        return options;
    }
};
PDFExportMarginComponent.ɵfac = function PDFExportMarginComponent_Factory(t) { return new (t || PDFExportMarginComponent)(); };
PDFExportMarginComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: PDFExportMarginComponent, selectors: [["kendo-pdf-export-margin"]], inputs: { left: "left", top: "top", right: "right", bottom: "bottom" }, decls: 0, vars: 0, template: function PDFExportMarginComponent_Template(rf, ctx) { }, encapsulation: 2 });
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportMarginComponent.prototype, "left", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportMarginComponent.prototype, "top", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportMarginComponent.prototype, "right", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportMarginComponent.prototype, "bottom", void 0);

/**
 * @hidden
 */
const compileTemplate = (templateRef) => {
    const context = {};
    let embeddedView = templateRef.createEmbeddedView(context);
    const result = (data) => {
        Object.assign(context, data);
        embeddedView.detectChanges();
        const templateWrap = document.createElement('span');
        embeddedView.rootNodes.forEach((rootNode) => {
            templateWrap.appendChild(rootNode.cloneNode(true));
        });
        return templateWrap;
    };
    result.destroy = () => {
        embeddedView.destroy();
        embeddedView = null;
    };
    return result;
};

/**
 * Represents the [Kendo UI PDF Export component for Angular]({% slug overview_pdfexport %}).
 *
 * @example
 * ```ts-preview
 * _@Component({
 *   selector: 'my-app',
 *   template: `
 *     <div class="example-config">
 *       <button kendoButton (click)="pdf.saveAs('document.pdf')">
 *         Save As PDF...
 *       </button>
 *     </div>
 *
 *     <kendo-pdf-export #pdf paperSize="A4" margin="2cm">
 *       Content goes here
 *     </kendo-pdf-export>
 *   `
 * })
 * export class AppComponent {
 * }
 * ```
 */
let PDFExportComponent = class PDFExportComponent {
    constructor(element) {
        this.element = element;
        /**
         * The creator of the PDF document.
         * @default "Kendo UI PDF Generator"
         */
        this.creator = 'Kendo UI PDF Generator';
        /**
         * Specifies the name of the exported PDF file.
         * @default "Export.pdf"
         */
        this.fileName = 'export.pdf';
        validatePackage(packageMetadata);
    }
    get drawMargin() {
        const marginComponent = this.marginComponent;
        let margin = this.margin;
        if (marginComponent) {
            margin = Object.assign(margin || {}, marginComponent.options);
        }
        return margin;
    }
    /**
     * Saves the content as a PDF file with the specified name.
     * @param fileName - The name of the exported file.
     */
    saveAs(fileName = this.fileName) {
        this.save(this.element.nativeElement, fileName);
    }
    /**
     * Exports the content as a `Group` for further processing.
     *
     * @return - The root group of the exported scene.
     */
    export() {
        return this.exportElement(this.element.nativeElement);
    }
    save(element, fileName) {
        this.exportElement(element)
            .then(group => this.exportGroup(group, this.pdfOptions()))
            .then(dataUri => this.saveDataUri(dataUri, fileName, this.saveOptions()));
    }
    exportElement(element) {
        const promise = this.drawElement(element, this.drawOptions());
        const cleanup = this.cleanup.bind(this);
        promise.then(cleanup, cleanup);
        return promise;
    }
    cleanup() {
        if (this.pageTemplate) {
            this.pageTemplate.destroy();
            delete this.pageTemplate;
        }
    }
    drawOptions() {
        if (this.pageTemplateDirective) {
            this.pageTemplate = compileTemplate(this.pageTemplateDirective.templateRef);
        }
        return {
            avoidLinks: this.avoidLinks,
            forcePageBreak: this.forcePageBreak,
            keepTogether: this.keepTogether,
            margin: this.drawMargin,
            paperSize: this.paperSize,
            landscape: this.landscape,
            repeatHeaders: this.repeatHeaders,
            scale: this.scale,
            template: this.pageTemplate
        };
    }
    pdfOptions() {
        return {
            autoPrint: this.autoPrint,
            author: this.author,
            creator: this.creator,
            date: this.date,
            imgDPI: this.imageResolution,
            keywords: this.keywords,
            landscape: this.landscape,
            margin: this.drawMargin,
            multiPage: true,
            paperSize: this.paperSize,
            producer: this.producer,
            subject: this.subject,
            title: this.title
        };
    }
    saveOptions() {
        return {
            forceProxy: this.forceProxy,
            proxyData: this.proxyData,
            proxyTarget: this.proxyTarget,
            proxyURL: this.proxyURL
        };
    }
    drawElement(element, options) {
        return drawDOM(element, options);
    }
    exportGroup(group, options) {
        return exportPDF(group, options);
    }
    saveDataUri(dataUri, fileName, options) {
        saveAs(dataUri, fileName, options);
    }
};
PDFExportComponent.ɵfac = function PDFExportComponent_Factory(t) { return new (t || PDFExportComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef)); };
PDFExportComponent.ɵcmp = ɵngcc0.ɵɵdefineComponent({ type: PDFExportComponent, selectors: [["kendo-pdf-export"]], contentQueries: function PDFExportComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        ɵngcc0.ɵɵcontentQuery(dirIndex, PDFExportTemplateDirective, true);
        ɵngcc0.ɵɵcontentQuery(dirIndex, PDFExportMarginComponent, true);
    } if (rf & 2) {
        var _t;
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.pageTemplateDirective = _t.first);
        ɵngcc0.ɵɵqueryRefresh(_t = ɵngcc0.ɵɵloadQuery()) && (ctx.marginComponent = _t.first);
    } }, inputs: { creator: "creator", fileName: "fileName", autoPrint: "autoPrint", author: "author", avoidLinks: "avoidLinks", forcePageBreak: "forcePageBreak", keepTogether: "keepTogether", date: "date", imageResolution: "imageResolution", forceProxy: "forceProxy", keywords: "keywords", landscape: "landscape", margin: "margin", paperSize: "paperSize", repeatHeaders: "repeatHeaders", scale: "scale", proxyData: "proxyData", proxyURL: "proxyURL", proxyTarget: "proxyTarget", producer: "producer", subject: "subject", title: "title" }, ngContentSelectors: _c0, decls: 2, vars: 0, template: function PDFExportComponent_Template(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵprojectionDef();
        ɵngcc0.ɵɵelementStart(0, "div");
        ɵngcc0.ɵɵprojection(1);
        ɵngcc0.ɵɵelementEnd();
    } }, encapsulation: 2 });
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], PDFExportComponent.prototype, "autoPrint", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "author", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportComponent.prototype, "avoidLinks", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "forcePageBreak", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "keepTogether", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "creator", void 0);
__decorate([
    Input(),
    __metadata("design:type", Date)
], PDFExportComponent.prototype, "date", void 0);
__decorate([
    Input(),
    __metadata("design:type", Number)
], PDFExportComponent.prototype, "imageResolution", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "fileName", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], PDFExportComponent.prototype, "forceProxy", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "keywords", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], PDFExportComponent.prototype, "landscape", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportComponent.prototype, "margin", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportComponent.prototype, "paperSize", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], PDFExportComponent.prototype, "repeatHeaders", void 0);
__decorate([
    Input(),
    __metadata("design:type", Number)
], PDFExportComponent.prototype, "scale", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], PDFExportComponent.prototype, "proxyData", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "proxyURL", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "proxyTarget", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "producer", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "subject", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], PDFExportComponent.prototype, "title", void 0);
__decorate([
    ContentChild(PDFExportTemplateDirective, { static: false }),
    __metadata("design:type", PDFExportTemplateDirective)
], PDFExportComponent.prototype, "pageTemplateDirective", void 0);
__decorate([
    ContentChild(PDFExportMarginComponent, { static: false }),
    __metadata("design:type", PDFExportMarginComponent)
], PDFExportComponent.prototype, "marginComponent", void 0);
PDFExportComponent = __decorate([ __metadata("design:paramtypes", [ElementRef])
], PDFExportComponent);

const COMPONENT_DIRECTIVES = [
    PDFExportComponent,
    PDFExportMarginComponent,
    PDFExportTemplateDirective
];
/**
 * Represents the [NgModule]({{ site.data.urls.angular['ngmoduleapi'] }}) definition for the PDF Export directive.
 */
let PDFExportModule = class PDFExportModule {
};
PDFExportModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: PDFExportModule });
PDFExportModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function PDFExportModule_Factory(t) { return new (t || PDFExportModule)(); } });
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportTemplateDirective, [{
        type: Directive,
        args: [{
                selector: '[kendoPDFTemplate]'
            }]
    }], function () { return [{ type: ɵngcc0.TemplateRef, decorators: [{
                type: Optional
            }] }]; }, null); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportMarginComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-pdf-export-margin',
                template: ``
            }]
    }], null, { left: [{
            type: Input
        }], top: [{
            type: Input
        }], right: [{
            type: Input
        }], bottom: [{
            type: Input
        }] }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportComponent, [{
        type: Component,
        args: [{
                selector: 'kendo-pdf-export',
                template: `<div><ng-content></ng-content></div>`
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }]; }, { creator: [{
            type: Input
        }], fileName: [{
            type: Input
        }], autoPrint: [{
            type: Input
        }], author: [{
            type: Input
        }], avoidLinks: [{
            type: Input
        }], forcePageBreak: [{
            type: Input
        }], keepTogether: [{
            type: Input
        }], date: [{
            type: Input
        }], imageResolution: [{
            type: Input
        }], forceProxy: [{
            type: Input
        }], keywords: [{
            type: Input
        }], landscape: [{
            type: Input
        }], margin: [{
            type: Input
        }], paperSize: [{
            type: Input
        }], repeatHeaders: [{
            type: Input
        }], scale: [{
            type: Input
        }], proxyData: [{
            type: Input
        }], proxyURL: [{
            type: Input
        }], proxyTarget: [{
            type: Input
        }], producer: [{
            type: Input
        }], subject: [{
            type: Input
        }], title: [{
            type: Input
        }], pageTemplateDirective: [{
            type: ContentChild,
            args: [PDFExportTemplateDirective, { static: false }]
        }], marginComponent: [{
            type: ContentChild,
            args: [PDFExportMarginComponent, { static: false }]
        }] }); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(PDFExportModule, { declarations: [PDFExportComponent, PDFExportMarginComponent, PDFExportTemplateDirective], exports: [PDFExportComponent, PDFExportMarginComponent, PDFExportTemplateDirective] }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(PDFExportModule, [{
        type: NgModule,
        args: [{
                declarations: [COMPONENT_DIRECTIVES],
                exports: [COMPONENT_DIRECTIVES]
            }]
    }], null, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { PDFExportComponent, PDFExportModule, PDFExportMarginComponent, PDFExportTemplateDirective, compileTemplate };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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